import { motion } from 'framer-motion';
import {
  Globe, Users, DollarSign, UserPlus, Activity,
  Loader2, RefreshCw, AlertCircle, MapPin, BarChart3, Eye, MousePointer, Monitor, Smartphone, Laptop, Tablet, ArrowDownRight,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { WebsiteAnalytics, getCountryName, DatePreset } from '@/hooks/useWebsiteAnalytics';
import { Button } from '@/components/ui/button';

interface WebsiteTrafficSectionProps {
  analytics: WebsiteAnalytics | null;
  loading: boolean;
  error: string | null;
  preset: DatePreset;
  onPresetChange: (preset: DatePreset) => void;
  onRefresh: () => void;
  dateRange: { start: Date; end: Date };
}

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e', expiring: '#eab308', expired: '#ef4444', inactive: '#6b7280',
};
const PLAN_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4'];
const SOURCE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#22c55e', '#ef4444', '#f97316'];
const DEVICE_COLORS: Record<string, string> = { mobile: '#8b5cf6', desktop: '#3b82f6', tablet: '#f59e0b', unknown: '#6b7280' };
const DEVICE_ICONS: Record<string, any> = { mobile: Smartphone, desktop: Laptop, tablet: Tablet };

const PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today', '7d': '7 days', '30d': '30 days', '90d': '90 days', custom: 'Custom',
};

const WebsiteTrafficSection = ({
  analytics, loading, error, preset, onPresetChange, onRefresh, dateRange,
}: WebsiteTrafficSectionProps) => {
  const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <>
      <motion.div className="mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-light text-foreground">Platform Analytics</h2>
            </div>
            <p className="text-sm text-muted-foreground">{formatDate(dateRange.start)} — {formatDate(dateRange.end)}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['today', '7d', '30d', '90d'] as DatePreset[]).map((p) => (
              <Button key={p} variant={preset === p ? 'default' : 'outline'} size="sm" className="text-xs h-8" onClick={() => onPresetChange(p)}>
                {PRESET_LABELS[p]}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {loading && <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}
      {!loading && error && (
        <div className="flex items-center justify-center gap-3 py-12 text-muted-foreground">
          <AlertCircle className="w-5 h-5 text-destructive" /><span className="text-sm">{error}</span>
          <Button variant="outline" size="sm" onClick={onRefresh}>Retry</Button>
        </div>
      )}
      {!loading && !error && analytics && <AnalyticsContent analytics={analytics} preset={preset} />}
    </>
  );
};

const AnalyticsContent = ({ analytics, preset }: { analytics: WebsiteAnalytics; preset: DatePreset }) => {
  const { timeSeries, summary, lists } = analytics;

  const formatLabel = (date: string) => {
    const d = new Date(date);
    return preset === 'today' ? `${d.getHours()}:00` : `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const trafficChartData = timeSeries.pageViews.data.map((point, i) => ({
    time: formatLabel(point.date),
    views: point.value,
    visitors: timeSeries.visitors.data[i]?.value || 0,
  }));

  const signupChartData = timeSeries.signups.data.map((point) => ({
    time: formatLabel(point.date),
    signups: point.value,
    active: timeSeries.activeUsers.data.find(p => p.date === point.date)?.value || 0,
  }));

  const revenueChartData = timeSeries.revenue.data.map((point) => ({
    time: formatLabel(point.date), revenue: point.value,
  }));

  const statusData = lists.status.data.map(item => ({
    name: item.label.charAt(0).toUpperCase() + item.label.slice(1),
    value: item.value, color: STATUS_COLORS[item.label] || '#6b7280',
  }));

  const planData = lists.plan.data.slice(0, 5).map((item, i) => ({
    name: item.label, value: item.value, color: PLAN_COLORS[i % PLAN_COLORS.length],
  }));

  const countryData = lists.country.data.slice(0, 8).map(item => ({
    code: item.label, name: getCountryName(item.label), count: item.value,
    percentage: summary.totalClients > 0 ? Math.round((item.value / summary.totalClients) * 100) : 0,
  }));

  const topPages = lists.topPages.data.slice(0, 10);
  const sourceData = lists.sources.data.slice(0, 8).map((item, i) => ({
    name: item.label, value: item.value, color: SOURCE_COLORS[i % SOURCE_COLORS.length],
  }));
  const deviceData = lists.devices.data.map(item => ({
    name: item.label.charAt(0).toUpperCase() + item.label.slice(1),
    value: item.value, color: DEVICE_COLORS[item.label] || '#6b7280',
  }));

  return (
    <>
      {/* Traffic Summary Cards */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
        {[
          { label: 'Page Views', value: summary.totalPageViews.toLocaleString(), icon: Eye, color: 'text-cyan-500' },
          { label: 'Visitors', value: summary.uniqueVisitors.toLocaleString(), icon: MousePointer, color: 'text-violet-500' },
          { label: 'Sessions', value: summary.totalSessions.toLocaleString(), icon: Monitor, color: 'text-orange-500' },
          { label: 'Pages/Session', value: summary.pagesPerSession.toFixed(1), icon: BarChart3, color: 'text-emerald-500' },
          { label: 'Bounce Rate', value: `${summary.bounceRate}%`, icon: ArrowDownRight, color: 'text-red-400' },
          { label: 'New Signups', value: summary.newSignups.toLocaleString(), icon: UserPlus, color: 'text-amber-500' },
        ].map((stat, index) => (
          <div key={index} className="p-4 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{stat.value}</p>
          </div>
        ))}
      </motion.div>

      {/* Page Views & Visitors Chart + Top Pages */}
      <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
        <div className="lg:col-span-2 p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" /> Page Views & Visitors
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trafficChartData}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} interval="preserveStartEnd" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="views" stroke="#06b6d4" fillOpacity={1} fill="url(#colorViews)" name="Page Views" />
                <Area type="monotone" dataKey="visitors" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorVisitors)" name="Visitors" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" /> Top Pages
          </h3>
          <div className="space-y-2.5">
            {topPages.length === 0 && <p className="text-xs text-muted-foreground">No page views recorded yet. Data will appear after publishing.</p>}
            {topPages.map((page, i) => (
              <div key={page.label} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                <span className="text-xs text-foreground flex-1 truncate font-mono">{page.label}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{page.value}</span>
                <div className="w-16 h-1.5 bg-muted/20 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500/60 rounded-full" style={{ width: `${topPages[0]?.value ? Math.min((page.value / topPages[0].value) * 100, 100) : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Sources + Devices + Countries */}
      <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}>
        {/* Traffic Sources */}
        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" /> Traffic Sources
          </h3>
          {sourceData.length === 0 ? (
            <p className="text-xs text-muted-foreground">No source data yet</p>
          ) : (
            <>
              <div className="h-40 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie data={sourceData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={4} dataKey="value">
                      {sourceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {sourceData.map(s => (
                  <div key={s.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-xs text-foreground flex-1">{s.name}</span>
                    <span className="text-xs text-muted-foreground">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Devices */}
        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-primary" /> Devices
          </h3>
          {deviceData.length === 0 ? (
            <p className="text-xs text-muted-foreground">No device data yet</p>
          ) : (
            <>
              <div className="h-40 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie data={deviceData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={4} dataKey="value">
                      {deviceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {deviceData.map(d => {
                  const Icon = DEVICE_ICONS[d.name.toLowerCase()] || Monitor;
                  const total = deviceData.reduce((s, x) => s + x.value, 0);
                  return (
                    <div key={d.name} className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5" style={{ color: d.color }} />
                      <span className="text-xs text-foreground flex-1">{d.name}</span>
                      <span className="text-xs text-muted-foreground">{total > 0 ? Math.round((d.value / total) * 100) : 0}%</span>
                      <span className="text-xs text-muted-foreground">{d.value}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Countries */}
        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" /> Top Countries
          </h3>
          <div className="space-y-2.5">
            {countryData.map((country, i) => (
              <div key={country.code} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                <span className="text-xs text-foreground flex-1 truncate">{country.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{country.count}</span>
                <div className="w-16 h-1.5 bg-muted/20 rounded-full overflow-hidden">
                  <div className="h-full bg-primary/60 rounded-full" style={{ width: `${Math.min(country.percentage, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Client Summary Cards */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
        {[
          { label: 'Total Clients', value: summary.totalClients.toLocaleString(), icon: Users, color: 'text-blue-500' },
          { label: 'Active', value: summary.totalActive.toLocaleString(), icon: Activity, color: 'text-green-500' },
          { label: 'Paid', value: summary.totalPaid.toLocaleString(), icon: DollarSign, color: 'text-purple-500' },
          { label: 'New Signups', value: summary.newSignups.toLocaleString(), icon: UserPlus, color: 'text-amber-500' },
          { label: 'Total Revenue', value: `$${Math.round(summary.totalRevenue).toLocaleString()}`, icon: DollarSign, color: 'text-green-500' },
        ].map((stat, index) => (
          <div key={index} className="p-4 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{stat.value}</p>
          </div>
        ))}
      </motion.div>

      {/* Signups & Active Users + Status */}
      <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}>
        <div className="lg:col-span-2 p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" /> Signups & Active Users
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={signupChartData}>
                <defs>
                  <linearGradient id="colorSignups" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} /><stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} interval="preserveStartEnd" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="signups" stroke="#22c55e" fillOpacity={1} fill="url(#colorSignups)" name="Signups" />
                <Area type="monotone" dataKey="active" stroke="#3b82f6" fillOpacity={1} fill="url(#colorActive)" name="Active" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" /> Client Status
          </h3>
          <div className="h-40 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={4} dataKey="value">
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {statusData.map(s => (
              <div key={s.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-xs text-foreground flex-1">{s.name}</span>
                <span className="text-xs text-muted-foreground">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Revenue + Plan Distribution */}
      <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" /> Revenue Timeline
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={9} interval="preserveStartEnd" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(v: number) => [`$${v}`, 'Revenue']} />
                <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-xl">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" /> Plan Distribution
          </h3>
          <div className="h-40 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie data={planData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={4} dataKey="value">
                  {planData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {planData.map(p => (
              <div key={p.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="text-xs text-foreground flex-1">{p.name}</span>
                <span className="text-xs text-muted-foreground">{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default WebsiteTrafficSection;
